import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Zap, 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Copy,
  Check,
  X,
  Command,
  Folder,
  Star,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// CANNED RESPONSE ITEM
// ============================================================================

const CannedResponseItem = memo(({ 
  response, 
  onSelect, 
  onEdit, 
  onDelete,
  isCompact = false 
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(response.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      onClick={() => onSelect?.(response)}
      className={cn(
        'group p-3 rounded-lg cursor-pointer transition-all',
        'hover:bg-indigo-50 dark:hover:bg-indigo-950/30',
        'border border-transparent hover:border-indigo-200 dark:hover:border-blue-800'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-sm text-slate-900 dark:text-zinc-50 truncate">
              {response.title}
            </h4>
            {response.shortcut && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 font-mono">
                {response.shortcut}
              </Badge>
            )}
          </div>
          <p className={cn(
            'text-xs text-slate-500 dark:text-zinc-400',
            isCompact ? 'line-clamp-1' : 'line-clamp-2'
          )}>
            {response.content}
          </p>
          {!isCompact && response.category && (
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-[10px]">
                <Folder className="w-3 h-3 mr-1" />
                {response.category}
              </Badge>
              {response.usage_count > 0 && (
                <span className="text-[10px] text-slate-500 flex items-center gap-1 dark:text-zinc-400">
                  <Clock className="w-3 h-3" />
                  {response.usage_count}x used
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleCopy}
            className="p-1.5 hover:bg-slate-200 dark:hover:bg-zinc-800 rounded"
            title="Copy"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
            )}
          </button>
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(response); }}
              className="p-1.5 hover:bg-slate-200 dark:hover:bg-zinc-800 rounded"
              title="Edit"
            >
              <Edit2 className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(response); }}
              className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/20 rounded dark:bg-red-950/40"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

CannedResponseItem.displayName = 'CannedResponseItem';

// ============================================================================
// CANNED RESPONSE EDITOR MODAL
// ============================================================================

const CannedResponseEditor = memo(({ 
  isOpen, 
  onClose, 
  response, // null for new, object for edit
  userId,
  token,
  onSaved 
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [shortcut, setShortcut] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (response) {
      setTitle(response.title || '');
      setContent(response.content || '');
      setShortcut(response.shortcut || '');
      setCategory(response.category || '');
    } else {
      setTitle('');
      setContent('');
      setShortcut('');
      setCategory('');
    }
  }, [response, isOpen]);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Judul dan konten wajib diisi');
      return;
    }

    setSaving(true);
    try {
      const data = {
        title: title.trim(),
        content: content.trim(),
        shortcut: shortcut.trim() || null,
        category: category.trim() || null,
        tags: [],
        is_global: false
      };

      if (response?.id) {
        // Update
        await axios.put(
          `${API}/api/canned-responses/${response.id}?user_id=${userId}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Template berhasil diupdate');
      } else {
        // Create
        await axios.post(
          `${API}/api/canned-responses?user_id=${userId}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Template berhasil dibuat');
      }

      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {response ? 'Edit Template' : 'Template Baru'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Judul *</Label>
            <Input
              id="title"
              placeholder="Contoh: Salam Pembuka"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="content">Konten *</Label>
            <Textarea
              id="content"
              placeholder="Tulis template pesan di sini..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shortcut">Shortcut</Label>
              <Input
                id="shortcut"
                placeholder="/greeting"
                value={shortcut}
                onChange={(e) => setShortcut(e.target.value)}
              />
              <p className="text-[10px] text-slate-500 dark:text-zinc-400">
                Ketik shortcut di chat untuk quick insert
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Kategori</Label>
              <Input
                id="category"
                placeholder="Greeting"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Batal
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {saving ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

CannedResponseEditor.displayName = 'CannedResponseEditor';

// ============================================================================
// QUICK REPLY PICKER (POPOVER)
// ============================================================================

export const QuickReplyPicker = memo(({ 
  userId, 
  token, 
  onSelect,
  trigger,
  align = 'start'
}) => {
  const [open, setOpen] = useState(false);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingResponse, setEditingResponse] = useState(null);
  const searchRef = useRef(null);

  const fetchResponses = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: userId });
      if (search) params.append('search', search);
      
      const response = await axios.get(
        `${API}/api/canned-responses?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setResponses(response.data.responses || []);
      }
    } catch (error) {
      console.error('Error fetching canned responses:', error);
      // Seed defaults on first load if empty
      if (error.response?.status === 404 || responses.length === 0) {
        try {
          await axios.post(
            `${API}/api/canned-responses/seed-defaults?user_id=${userId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          fetchResponses();
        } catch (seedError) {
          console.error('Error seeding defaults:', seedError);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [userId, token, search]);

  useEffect(() => {
    if (open) {
      fetchResponses();
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open, fetchResponses]);

  const handleSelect = async (response) => {
    onSelect?.(response.content);
    setOpen(false);
    
    // Increment usage count
    try {
      await axios.post(
        `${API}/api/canned-responses/${response.id}/use`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error incrementing usage:', error);
    }
  };

  const handleDelete = async (response) => {
    if (!confirm('Hapus template ini?')) return;
    
    try {
      await axios.delete(
        `${API}/api/canned-responses/${response.id}?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template dihapus');
      fetchResponses();
    } catch (error) {
      toast.error('Gagal menghapus template');
    }
  };

  // Group by category
  const groupedResponses = responses.reduce((acc, resp) => {
    const cat = resp.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(resp);
    return acc;
  }, {});

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {trigger || (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title="Quick Reply (Canned Response)"
            >
              <Zap className="w-4 h-4" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent 
          align={align} 
          className="w-80 p-0"
          sideOffset={8}
        >
          {/* Header */}
          <div className="p-3 border-b border-slate-200 dark:border-zinc-800">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                Quick Reply
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingResponse(null);
                  setEditorOpen(true);
                }}
                className="h-7 px-2 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Baru
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <Input
                ref={searchRef}
                placeholder="Cari atau ketik /shortcut..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>
          
          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : responses.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Zap className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 dark:text-zinc-400">Belum ada template</p>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingResponse(null);
                    setEditorOpen(true);
                  }}
                  className="mt-3"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Buat Template
                </Button>
              </div>
            ) : (
              <div className="p-2 space-y-3">
                {Object.entries(groupedResponses).map(([category, items]) => (
                  <div key={category}>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider px-2 mb-1 dark:text-zinc-400">
                      {category}
                    </p>
                    <div className="space-y-1">
                      {items.map((resp) => (
                        <CannedResponseItem
                          key={resp.id}
                          response={resp}
                          onSelect={handleSelect}
                          onEdit={(r) => {
                            setEditingResponse(r);
                            setEditorOpen(true);
                          }}
                          onDelete={handleDelete}
                          isCompact
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-2 border-t border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50">
            <p className="text-[10px] text-slate-500 text-center flex items-center justify-center gap-1 dark:text-zinc-400">
              <Command className="w-3 h-3" />
              Ketik <kbd className="px-1 bg-slate-200 dark:bg-zinc-800 rounded text-[9px]">/shortcut</kbd> di chat
            </p>
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Editor Modal */}
      <CannedResponseEditor
        isOpen={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingResponse(null);
        }}
        response={editingResponse}
        userId={userId}
        token={token}
        onSaved={fetchResponses}
      />
    </>
  );
});

QuickReplyPicker.displayName = 'QuickReplyPicker';

// ============================================================================
// CANNED RESPONSES MANAGER (FULL PAGE/PANEL)
// ============================================================================

export const CannedResponsesManager = memo(({ 
  userId, 
  token,
  className = '' 
}) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingResponse, setEditingResponse] = useState(null);

  const fetchResponses = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: userId });
      if (search) params.append('search', search);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      
      const response = await axios.get(
        `${API}/api/canned-responses?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setResponses(response.data.responses || []);
      }
    } catch (error) {
      console.error('Error fetching canned responses:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, token, search, selectedCategory]);

  const fetchCategories = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await axios.get(
        `${API}/api/canned-responses/categories?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchResponses();
    fetchCategories();
  }, [fetchResponses, fetchCategories]);

  const handleDelete = async (response) => {
    if (!confirm('Hapus template ini?')) return;
    
    try {
      await axios.delete(
        `${API}/api/canned-responses/${response.id}?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template dihapus');
      fetchResponses();
      fetchCategories();
    } catch (error) {
      toast.error('Gagal menghapus template');
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Quick Replies
          </h2>
          <p className="text-sm text-slate-500 dark:text-zinc-400">Template balasan cepat untuk agent</p>
        </div>
        <Button
          onClick={() => {
            setEditingResponse(null);
            setEditorOpen(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Template Baru
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
          <Input
            placeholder="Cari template..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-slate-200 dark:border-zinc-800 rounded-lg text-sm bg-white dark:bg-zinc-900"
        >
          <option value="all">Semua Kategori</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : responses.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800">
          <Zap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-1">
            Belum ada template
          </h3>
          <p className="text-sm text-slate-500 mb-4 dark:text-zinc-400">
            Buat template untuk mempercepat balasan
          </p>
          <Button
            onClick={() => {
              setEditingResponse(null);
              setEditorOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Buat Template Pertama
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {responses.map((resp) => (
            <div 
              key={resp.id}
              className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4"
            >
              <CannedResponseItem
                response={resp}
                onEdit={(r) => {
                  setEditingResponse(r);
                  setEditorOpen(true);
                }}
                onDelete={handleDelete}
              />
            </div>
          ))}
        </div>
      )}

      {/* Editor */}
      <CannedResponseEditor
        isOpen={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingResponse(null);
        }}
        response={editingResponse}
        userId={userId}
        token={token}
        onSaved={() => {
          fetchResponses();
          fetchCategories();
        }}
      />
    </div>
  );
});

CannedResponsesManager.displayName = 'CannedResponsesManager';

export default QuickReplyPicker;
